/**
 * Programmer l'envoi d'un mail (PLAN-MAIL-ENVOI-PROGRAMME) — the pure part: the one-click times
 * offered under the send arrow, the date check, the words of the server's refusals and of a held
 * send. The company's time zone throughout (a parameter: `useOrganizationTimeZone()`,
 * PLAN-GENERICITE § 4 quinquies step 7). The server re-checks everything (`schedule_mail_send`:
 * 2 minutes to 90 days); these helpers only spare a refused click.
 */
import { z } from 'zod';

import { fromZonedInputValue } from '../appointments/proposalView';
import { dayInZone } from '../organization/timeZone';

const MIN_DELAY_MS = 5 * 60_000;
const MAX_DELAY_MS = 90 * 24 * 60 * 60_000;

/** How the time was chosen — stored with the send, shown back in the banner. */
export interface ScheduleTiming {
  source: 'preset' | 'custom' | 'best';
  label: string;
  reason?: string;
}

export interface SendLaterPreset {
  key: 'demain-matin' | 'demain-aprem' | 'lundi';
  label: string;
  sendAt: string;
}

function addDays(day: string, n: number): { day: string; weekday: number } {
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + n));
  return { day: date.toISOString().slice(0, 10), weekday: date.getUTCDay() };
}

const isWorkday = (weekday: number) => weekday >= 1 && weekday <= 5;

/**
 * Tomorrow 8 h and 14 h when tomorrow is a working day; next Monday 8 h from Thursday to
 * Saturday (on Sunday, « Demain 8 h » is already Monday). A business mail is not scheduled
 * for a weekend in one click — the date field remains for that.
 */
export function sendLaterPresets(now: Date, timeZone: string): SendLaterPreset[] {
  const at = (day: string, hour: string): string | null => fromZonedInputValue(`${day}T${hour}`, timeZone);
  const today = dayInZone(now, timeZone);
  const tomorrow = addDays(today, 1);
  const presets: SendLaterPreset[] = [];
  if (isWorkday(tomorrow.weekday)) {
    const morning = at(tomorrow.day, '08:00');
    const afternoon = at(tomorrow.day, '14:00');
    if (morning)
      presets.push({
        key: 'demain-matin',
        label: 'Demain 8 h',
        sendAt: morning,
      });
    if (afternoon)
      presets.push({
        key: 'demain-aprem',
        label: 'Demain 14 h',
        sendAt: afternoon,
      });
  }
  const weekday = addDays(today, 0).weekday;
  if (weekday >= 4 && weekday <= 6) {
    const monday = at(addDays(today, (8 - weekday) % 7).day, '08:00');
    if (monday) presets.push({ key: 'lundi', label: 'Lundi 8 h', sendAt: monday });
  }
  return presets;
}

/** Null when the server will accept the time; else what to say. */
export function scheduleDateError(sendAt: string | null, now: Date): string | null {
  const t = sendAt ? Date.parse(sendAt) : Number.NaN;
  if (Number.isNaN(t)) return 'Choisis une date et une heure.';
  if (t - now.getTime() < MIN_DELAY_MS) return 'Choisis un moment au moins 5 minutes plus tard.';
  if (t - now.getTime() > MAX_DELAY_MS) return 'Pas plus de 90 jours à l’avance.';
  return null;
}

const timeOf = (date: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).formatToParts(date);
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return minute === '00' ? `${Number(hour)} h` : `${Number(hour)} h ${minute}`;
};

/** The confirmation every surface shows after « Programmer » (one sentence, one place). */
export function scheduledConfirmation(sendAt: string, now: Date, timeZone: string): string {
  return `Mail programmé : ${formatSendAt(sendAt, now, timeZone)}.`;
}

/** « aujourd'hui à 14 h 30 », « demain à 8 h », « lundi 12 oct. à 8 h » — in the given zone. */
export function formatSendAt(iso: string, now: Date, timeZone: string): string {
  const date = new Date(iso);
  const day = dayInZone(date, timeZone);
  const today = dayInZone(now, timeZone);
  if (day === today) return `aujourd’hui à ${timeOf(date, timeZone)}`;
  if (day === addDays(today, 1).day) return `demain à ${timeOf(date, timeZone)}`;
  const label = new Intl.DateTimeFormat('fr-FR', {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  }).format(date);
  return `${label} à ${timeOf(date, timeZone)}`;
}

const REFUSALS: Record<string, string> = {
  send_at_invalid: 'Choisis un moment au moins 5 minutes plus tard, et à moins de 90 jours.',
  mailbox_missing: 'Aucune boîte Gmail CRM connectée à ton compte : connecte-la dans les Réglages.',
  already_scheduled: 'Un envoi est déjà programmé sur ce fil : change-le depuis le bandeau du fil.',
  thread_other_mailbox: 'Ce fil est dans la boîte d’une autre personne : programme la réponse depuis ta propre copie de la conversation.',
  recipients_invalid: 'Il faut au moins un destinataire (50 au plus).',
  subject_missing: 'L’objet est vide.',
  body_missing: 'Le mail est vide.',
  attachments_invalid: 'Dix pièces jointes au plus.',
  payload_invalid: 'Ce mail ne peut pas être programmé.',
  not_reschedulable: 'Cet envoi n’est plus modifiable (déjà parti, annulé, ou à vérifier dans Gmail).',
  not_found: 'Cet envoi programmé n’existe plus.',
  autonomous_sending: 'Otto envoie ce brouillon en ce moment : rien n’est programmé. Relis le fil dans une minute.',
  draft_not_found: 'Ce brouillon est introuvable : il a été remplacé, ou il n’est pas le tien. Relis le fil.',
  payload_too_large: 'Ce mail est trop lourd pour être programmé : envoie-le maintenant.',
};

/** The sentence for a server refusal code (`not_cancelable:sent` reads as « déjà parti »). */
export function scheduleRefusalMessage(code: string | null | undefined): string {
  if (!code) return 'Action impossible. Réessaie.';
  if (code.startsWith('not_cancelable:')) {
    return code.endsWith(':sent') ? 'Ce mail est déjà parti.' : 'Cet envoi n’est plus annulable.';
  }
  return REFUSALS[code] ?? 'Action impossible. Réessaie.';
}

/** Why a scheduled mail did not leave, and what to do about it. */
export function holdReasonText(reason: string | null): string {
  switch (reason) {
    case 'envoi_refuse':
      return 'Pas parti : Gmail a refusé l’envoi, rien n’est parti. Corrige (boîte Gmail, pièce jointe), puis envoie, reprogramme ou annule.';
    case 'client_replied':
      return 'Retenu : le client a écrit depuis que tu l’as programmé. Relis le fil, puis envoie, reprogramme ou annule.';
    case 'already_answered':
      return 'Retenu : une réponse est déjà partie sur ce fil. Relis-la avant d’envoyer celui-ci.';
    case 'etat_inconnu':
      return 'L’envoi a été interrompu : ce mail est peut-être parti. Vérifie dans Gmail avant tout renvoi.';
    case 'en_retard':
      return 'Retenu : il serait parti plus de 2 h après l’heure choisie. Envoie-le maintenant, reprogramme-le ou annule-le.';
    default:
      return 'Retenu : relis le fil avant de l’envoyer.';
  }
}

/** One scheduled send, as `list_scheduled_mail_sends` returns it (pass-through). */
export const scheduledMailSendSchema = z.object({
  id: z.string(),
  thread_key: z.string().nullable(),
  ai_draft_id: z.string().nullable(),
  send_at: z.string(),
  status: z.enum(['scheduled', 'sending', 'sent', 'held', 'canceled']),
  hold_reason: z.string().nullable(),
  error: z.string().nullable(),
  timing: z
    .object({
      source: z.string().optional(),
      label: z.string().optional(),
      reason: z.string().optional(),
    })
    .passthrough()
    .nullable(),
  to: z.array(z.string()).nullable(),
  cc: z.array(z.string()).nullable(),
  subject: z.string().nullable(),
  body_html: z.string().nullable(),
  attachments: z.number(),
  created_by: z.string(),
  created_at: z.string(),
  sent_at: z.string().nullable(),
  /** The thread moved since the decision: it will be held at its time (said in the banner). */
  thread_moved: z.string().nullable().optional(),
});
export type ScheduledMailSend = z.infer<typeof scheduledMailSendSchema>;

/**
 * The banner's list, row by row: one unreadable row is dropped (and counted, for the log), the
 * others still show — a scheduled mail never disappears from the screen because of its neighbour.
 */
export function readScheduledSends(data: unknown): {
  sends: ScheduledMailSend[];
  unreadable: number;
} {
  if (!Array.isArray(data)) return { sends: [], unreadable: data == null ? 0 : 1 };
  const sends: ScheduledMailSend[] = [];
  for (const row of data) {
    const parsed = scheduledMailSendSchema.safeParse(row);
    if (parsed.success) sends.push(parsed.data);
  }
  return { sends, unreadable: data.length - sends.length };
}

/** Said before the time comes, so that nothing surprises: the base will hold it. */
export function threadMovedText(reason: string | null | undefined): string | null {
  if (reason === 'client_replied')
    return 'Le client a écrit depuis : ce mail sera retenu à l’heure prévue. Relis le fil.';
  if (reason === 'already_answered') return 'Une réponse est partie depuis : ce mail sera retenu à l’heure prévue.';
  return null;
}

/** Can the person still change it? A send that may have left is checked in Gmail, not resent. */
export function isEditable(send: Pick<ScheduledMailSend, 'status' | 'hold_reason'>): boolean {
  return send.status === 'scheduled' || (send.status === 'held' && send.hold_reason !== 'etat_inconnu');
}
